import React, { useState } from 'react';
import { Stack, Text, Button, Group, Skeleton, Box } from '@mantine/core';
import BigNumber from 'bignumber.js';
import cn from 'clsx';
import { FaCog } from 'react-icons/fa';

import { NumberInput } from 'src/components/NumberInput/NumberInput';
import { WalletSuspense } from 'src/components/WalletSuspense/WalletSuspense';
import { ErrorDisplay } from 'src/components/ErrorDisplay/ErrorDisplay';
import { Rows } from 'src/components/Rows/Rows';
import { Token } from 'src/components/Token/Token';
import { SwapSettingsModal } from 'src/components/SwapSettingsModal/SwapSettingsModal';
import { SwapConfirmationModal } from 'src/components/SwapConfirmationModal/SwapConfirmationModal';
import { formatDecimal, formatUnits } from 'src/utils';
import { usePrepareSwap } from 'src/hooks/usePrepareSwap';
import {
  useSharesBalance,
  useAssetsToSharesRatio,
} from 'src/api/smart-contracts';
import { useStakingIsPaused } from 'src/api/smart-contracts';
import { SHOW_GAS_FEES, NETWORK_VARS } from 'src/constants';
import { useOracleInfo, useWalletRequests } from 'src/api';
import { Quote } from 'src/types';
import { useRequestRedeem } from './useRequestRedeem';

export function RequestPanel({
  onRequestSuccess,
}: {
  onRequestSuccess?: () => void;
}) {
  const { stakingIsPaused, stakingIsPausedMessage } = useStakingIsPaused();
  const { sharesBalanceFormatted, isLoadingSharesBalance } = useSharesBalance();
  const { sharesToAssetsRatio, isLoadingRatio } = useAssetsToSharesRatio();

  const { atLimit, isLoadingWalletRequests } = useWalletRequests();

  const [value, setValue] = useState<string | undefined>();
  const [withdrawalMethod, setWithdrawalMethod] = useState<
    'capricorn' | 'pool'
  >('capricorn');

  const [slippageValue, setSlippageValue] = useState<string>('auto');
  const [customSlippageValue, setCustomSlippageValue] = useState<
    string | undefined
  >();
  const [deadlineValue, setDeadlineValue] = useState<string | undefined>('20');

  const [settingsModalOpened, setSettingsModalOpened] = useState(false);

  const [quoteToApprove, setQuoteToApprove] = useState<Quote | undefined>();
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);
  const [confirmationModalError, setConfirmationModalError] = useState<
    Error | undefined
  >();

  const valueBN = BigNumber(value || 0);
  const isSwapMethod = withdrawalMethod === 'pool';

  const { requestWaitTimeHR, isLoadingOracleInfo } = useOracleInfo();
  const {
    isRequesting,
    requestError,
    transactionCost,
    isLoadingTransactionCost,
    requestRedeem,
  } = useRequestRedeem({
    amount: value || '0',
    onSuccess: (timestamp) => {
      onRequestSuccess?.();
      setValue(undefined);
    },
  });

  const {
    quote,
    tokenInBalance,
    isLoadingQuote,
    quoteError,
    notEnoughBalanceForGas,
  } = usePrepareSwap({
    amount: BigNumber(value || '0')
      .times(1e18)
      .toFixed(),
    tokenIn: NETWORK_VARS.tokensAddresses.APRMON,
    tokenOut: NETWORK_VARS.tokensAddresses.MON,
    tradeType: 'EXACT_INPUT',
    slippage: 'auto',
  });

  const handleSwapConfirmationModalClose = () => {
    setConfirmationModalOpened(false);
    setQuoteToApprove(undefined);
  };

  const isInsufficientBalance = BigNumber(valueBN).gt(sharesBalanceFormatted);

  const getSubmitErrorMessage = () => {
    if (BigNumber(valueBN).lte(0)) {
      return 'Enter an amount';
    }

    if (isInsufficientBalance) {
      return 'Insufficient balance';
    }

    if (atLimit) {
      return 'Max withdraw count';
    }

    return '';
  };

  const submitErrorMessage = getSubmitErrorMessage();
  const submitDisabled =
    stakingIsPaused || !!submitErrorMessage || !requestRedeem || atLimit;

  const handleSubmit = () => {
    if (submitDisabled || (isSwapMethod && isLoadingQuote)) {
      return;
    }

    if (isSwapMethod) {
      setQuoteToApprove(structuredClone(quote));
      setConfirmationModalOpened(true);
      setConfirmationModalError(undefined);
    } else {
      requestRedeem();
    }
  };

  const waitTime =
    isLoadingOracleInfo || isLoadingWalletRequests ? (
      <Skeleton w={80} h={23} />
    ) : (
      requestWaitTimeHR
    );

  return (
    <Stack gap={24}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        className={cn(
          (stakingIsPaused || isRequesting) && 'pointer-events-none'
        )}
      >
        <Stack gap={44}>
          <Stack gap={24}>
            <NumberInput
              value={value}
              label={
                <Group justify="space-between" w="100%">
                  <Text>Unstake aprMON</Text>
                  <Token.Balance
                    value={sharesBalanceFormatted}
                    useSCBalance={false}
                    tokenInfo={{
                      contract_address: NETWORK_VARS.tokensAddresses.APRMON,
                      decimals: 18,
                      symbol: 'aprMON',
                    }}
                    color={isInsufficientBalance ? '#FF3E55' : undefined}
                  />
                </Group>
              }
              tokenSymbol="__APRMON__"
              inputProps={{
                size: 'sm',
                labelProps: { w: '100%' },
                classNames: {
                  root: 'bg-[#151519] rounded-[16px] border-none px-3 py-4',
                  label: 'px-2 mb-3',
                  wrapper: '!px-2 bg-transparent',
                  input:
                    'h-[32px] pl-[40px] border-none bg-transparent text-[#A0A0B1] text-[24px] font-[700]',
                },
              }}
              leftSectionProps={{
                className: 'px-2 h-[32px] w-[48px]',
              }}
              rightSectionProps={{
                className: 'w-[54px] px-0',
              }}
              onChange={setValue}
              onMaxClick={() => setValue(sharesBalanceFormatted)}
            />

            <Group gap={14} className="max-lg:flex-col">
              <ChoiceCard
                title={
                  <Text fz={14} fw={600}>
                    Use Capricorn
                  </Text>
                }
                rows={[
                  {
                    label: 'Rate',
                    value: isLoadingRatio ? (
                      <Skeleton w={80} h={23} />
                    ) : (
                      <>
                        1 :{' '}
                        {formatDecimal(sharesToAssetsRatio, {
                          withTruncate: true,
                        })}
                      </>
                    ),
                  },
                  {
                    label: 'Wait time',
                    value: waitTime,
                  },
                  {
                    label: 'You receive',
                    value: (
                      <>
                        {formatDecimal(
                          BigNumber(value || 0).times(sharesToAssetsRatio),
                          { withTruncate: true }
                        )}{' '}
                        MON
                      </>
                    ),
                  },
                ]}
                isActive={withdrawalMethod === 'capricorn'}
                onClick={() => setWithdrawalMethod('capricorn')}
              />
              <ChoiceCard
                title={
                  <Group justify="space-between" w="100%">
                    <Text fz={14} fw={600}>
                      Use Pool
                    </Text>
                    <FaCog
                      size={16}
                      className="cursor-pointer"
                      color="#7E7E95"
                      onClick={() => setSettingsModalOpened(true)}
                    />
                  </Group>
                }
                rows={[
                  {
                    label: 'Rate',
                    value: isLoadingQuote ? (
                      <Skeleton w={100} h={20} />
                    ) : quote?.outAmount ? (
                      <>
                        1 :{' '}
                        {formatDecimal(
                          BigNumber(formatUnits(quote?.outAmount || '1')).div(
                            formatUnits(quote?.inAmount || '1')
                          ),
                          { withTruncate: true }
                        )}
                      </>
                    ) : (
                      '-'
                    ),
                  },
                  { label: 'Wait time', value: 'Instant' },
                  {
                    label: 'You receive',
                    value: isLoadingQuote ? (
                      <Skeleton w={100} h={20} />
                    ) : quote?.outAmount ? (
                      <>
                        {formatDecimal(formatUnits(quote.outAmount), {
                          withTruncate: true,
                        })}{' '}
                        MON
                      </>
                    ) : (
                      '-'
                    ),
                  },
                ]}
                isActive={isSwapMethod}
                onClick={() => setWithdrawalMethod('pool')}
              />
            </Group>
          </Stack>

          <Stack gap={16}>
            <WalletSuspense buttonProps={{ size: 'lg' }}>
              <Button
                variant="gradient"
                size="lg"
                type="submit"
                loading={isRequesting || (isSwapMethod && isLoadingQuote)}
                disabled={submitDisabled}
              >
                {stakingIsPaused
                  ? stakingIsPausedMessage
                  : submitErrorMessage ||
                    (isSwapMethod ? 'Swap' : 'Request Withdrawal')}
              </Button>

              {!submitErrorMessage && (
                <ErrorDisplay>
                  {requestError?.message ||
                    (isSwapMethod
                      ? confirmationModalError?.message
                      : undefined)}
                </ErrorDisplay>
              )}
            </WalletSuspense>

            <Stack gap={8}>
              <Rows
                items={[
                  ...(SHOW_GAS_FEES
                    ? [
                        {
                          label: 'Estimated transaction cost',
                          value: isLoadingTransactionCost ? (
                            <Skeleton w={160} h={23} />
                          ) : transactionCost.eq(0) ? (
                            '-'
                          ) : (
                            <>
                              {formatDecimal(transactionCost, {
                                withTruncate: true,
                              })}{' '}
                              MON
                            </>
                          ),
                        },
                      ]
                    : []),
                  {
                    label: 'Estimated wait time',
                    value: waitTime,
                  },
                ]}
              />
            </Stack>
          </Stack>
        </Stack>
      </form>

      {settingsModalOpened && (
        <SwapSettingsModal
          slippageValue={slippageValue}
          customSlippageValue={customSlippageValue}
          deadlineValue={deadlineValue}
          onSlippageChange={(newValue: string) => {
            setSlippageValue(newValue);
            if (newValue !== 'custom') {
              setCustomSlippageValue(undefined);
            }
          }}
          onCustomSlippageChange={setCustomSlippageValue}
          onDeadlineChange={setDeadlineValue}
          onClose={() => setSettingsModalOpened(false)}
        />
      )}

      {confirmationModalOpened && (
        <SwapConfirmationModal
          fromValue={value || '0'}
          toValue={formatUnits(quote?.outAmount || '0')}
          fromToken={NETWORK_VARS.tokensAddresses.APRMON}
          toToken={NETWORK_VARS.tokensAddresses.MON}
          quote={quoteToApprove!}
          onClose={handleSwapConfirmationModalClose}
          onSuccess={() => {
            setValue(undefined);
            handleSwapConfirmationModalClose();
          }}
          onError={(err) => {
            setConfirmationModalError(err);
            handleSwapConfirmationModalClose();
          }}
        />
      )}
    </Stack>
  );
}

function ChoiceCard({
  title,
  rows,
  isActive,
  disabled,
  onClick,
}: {
  title: React.ReactNode;
  rows: { label: string; value: React.ReactNode }[];
  isActive?: boolean;
  disabled?: boolean;
  onClick?: () => void;
}) {
  return (
    <Box
      px={16}
      py={12}
      className={cn(
        'relative flex-grow flex-1 shrink-0 max-lg:w-full outline outline-solid outline-[#202027] rounded-[16px]',
        isActive && 'outline-primarybrand',
        disabled && 'opacity-50 pointer-events-none',
        !disabled && 'cursor-pointer'
      )}
      onClick={onClick}
    >
      <Stack gap={6}>
        <Group justify="space-between">
          {title}

          {disabled && <Text>Coming soon</Text>}
        </Group>

        <Stack gap={6}>
          <Rows items={rows} />
        </Stack>
      </Stack>
    </Box>
  );
}
